import React from "react";

import Hero from "../../../components/Hero";
import LeaderBoard from "../../../components/LeaderBoard";
import TeamYetiCard from "../TeamYetiCard";
import AnticipatedEarnings from "../../../components/AnticipatedEarnings";
import MilestoneBonuses from "../../../components/MilestoneBonuses";
import ProjectedAnnualComp from "../../../components/ProjectedAnnualComp";
import ScopedBeatYeti from "../../../components/CardScope/ScopedBeatYeti";
import ScopedCard from "../../../components/CardScope/ScopedCard";
import ScopedGameboard from "../../../components/CardScope/ScopedGameboard";
import ScopedPodium from "../../../components/CardScope/ScopedPodium";
import LeaderboardTable from "../../../components/LeaderBoard/LeaderboardTable";
import LaborMetrics from "../../../components/LaborMetrics";
import GoalProgress from "../../../components/GoalProgress";
import ProfitTrend from "../../../components/ProfitTrend";
import NetProfitBonus from "../../../components/NetProfitBonus";
import DeptMetrics from "../../../components/DeptMetrics";
import ScopedCustomWidget from "../../../components/CardScope/ScopedCustomWidget";
import { sanitizePayoutCurve, sanitizePieceLayout } from "../../../components/CustomWidget/payoutCurve";
import { sanitizeBonus, isStepBonus } from "../../../components/CustomWidget/bonusSteps";
import ScopedBonusCard from "../../../components/CardScope/ScopedBonusCard";
import { normalizeLookAhead } from "../../../components/GoalProgress/lookAhead";
import { yetiByTeamOn, normalizeYetiGoal, normalizeYetiGoals } from "../../../helpers/beatYeti";
import {
  PreviewHero,
  PreviewWidgets,
  PreviewKotm,
  PreviewKotmPeak,
  PreviewYeti,
  PreviewLeaderboard,
  PreviewPodium,
  PreviewEarnings,
  PreviewEarningsLedger,
  PreviewEarningsRows,
  PreviewMilestones,
  PreviewMilestones2,
  PreviewBeatYeti,
  PreviewBeatYetiGates,
  PreviewLabor,
  PreviewGoalProgress,
  PreviewProfit,
  PreviewNetBonus,
  PreviewDeptMetrics,
} from "./widgetPreviews";

/**
 * The dashboard component registry.
 *
 * Every block that can live on the customizable workspace dashboard is
 * declared here once: identity (id/title/blurb/group), grid constraints
 * (12-column react-grid-layout units), and how it renders. The customize
 * drawer, the grid, and the default layout all read from this list — adding
 * a future component (earnings, milestones, …) is one new entry.
 *
 * Grid math: item height in px = h * ROW_HEIGHT + (h - 1) * GRID_MARGIN.
 * With ROW_HEIGHT 8 and margin 32 that is 40h − 32 px.
 *
 * The 32px margins reproduce the original dashboard's MUI spacing={4}
 * gutters — the default board must look exactly like the page always did.
 *
 * 24 columns (not 12) so widths can land on "half column" boundaries —
 * e.g. the podium at 5/24 = 2.5 of a classic 12-col grid. A width of
 * n in the old 12-col units is 2n here; the rendered pixels come out
 * identical because gutters only exist between blocks.
 */

export const GRID_COLS = 24;
export const ROW_HEIGHT = 8;
export const GRID_MARGIN = [32, 32];

/* Row icons for the add-component drawer — quiet stroke glyphs, currentColor
   so they inherit the brand tint from the row's icon square. */
const svg = (children) => () => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {children}
  </svg>
);
const IconHero = svg(<><circle cx="12" cy="8" r="3.2" /><path d="M5.5 20a6.5 6.5 0 0 1 13 0" /></>);
const IconWidgets = svg(<><rect x="3" y="3" width="7" height="7" rx="1.5" /><rect x="14" y="3" width="7" height="7" rx="1.5" /><rect x="3" y="14" width="7" height="7" rx="1.5" /><rect x="14" y="14" width="7" height="7" rx="1.5" /></>);
const IconKotm = svg(<path d="M3 20l6-11 4 6 3-5 5 10z" />);
const IconYeti = svg(<><path d="M4 19V5" /><path d="M4 19h16" /><rect x="7" y="12" width="3" height="4" rx="0.6" /><rect x="12" y="9" width="3" height="7" rx="0.6" /><rect x="17" y="6" width="3" height="10" rx="0.6" /></>);
const IconLeaderboard = svg(<><path d="M4 6h10" /><path d="M4 12h13" /><path d="M4 18h8" /><circle cx="20" cy="6" r="1" /></>);
const IconPodium = svg(<><rect x="9" y="8" width="6" height="12" rx="1" /><rect x="3" y="12" width="6" height="8" rx="1" /><rect x="15" y="11" width="6" height="9" rx="1" /></>);
const IconEarnings = svg(<><path d="M12 2v20" /><path d="M17 6.5C17 4.6 14.8 3.5 12 3.5S7 4.6 7 6.5s2.2 2.7 5 3.2 5 1.3 5 3.3-2.2 3-5 3-5-1.1-5-3" /></>);
const IconMilestones = svg(<><path d="M6 21V4" /><path d="M6 4h11l-2 3 2 3H6" /></>);
const IconBeatYeti = svg(<><path d="M12 3l7 3v5c0 5-3 8-7 10-4-2-7-5-7-10V6z" /><path d="M9 12l2 2 4-4" /></>);
const IconBuild = svg(<><rect x="3" y="3" width="18" height="18" rx="2.5" strokeDasharray="3 3" /><path d="M12 8v8M8 12h8" /></>);
const IconLabor = svg(<><path d="M4 7h16" /><path d="M4 12h11" /><path d="M4 17h7" /><path d="M19 13v6" /></>);
const IconGoal = svg(<><circle cx="12" cy="12" r="8.5" /><circle cx="12" cy="12" r="4.3" /><circle cx="12" cy="12" r="0.6" fill="currentColor" /></>);
const IconProfit = svg(<><path d="M3 20h18" /><path d="M4 15l5-5 3 3 4-6 4 4" /><path d="M4 8.5h16" strokeDasharray="3 3" /></>);
const IconNetBonus = svg(<><path d="M3 20h18" /><path d="M5 20v-4h4v4M10 20v-8h4v8M15 20V7h4v13" /><path d="M17 3.5v0.01" /></>);
const IconDeptMetrics = svg(<><rect x="3" y="4" width="18" height="16" rx="2" /><path d="M3 10h18M3 15h18M9 4v16" /></>);

/** On a rollup board (Head of Sales), force combined workspaces onto the card
 *  unless it already has an explicit 2+ pick that excludes the empty host. */
const withInheritedWorkspaces = (s, inheritWorkspaceIds, hostWorkspaceId = null) => {
  const ownIds = Array.isArray(s?.workspaceIds) ? s.workspaceIds.filter(Boolean).map(String) : [];
  const combined = Array.isArray(inheritWorkspaceIds) ? inheritWorkspaceIds.filter(Boolean).map(String) : [];
  if (combined.length < 2) return s || {};
  const host = hostWorkspaceId ? String(hostWorkspaceId) : null;
  const nonHost = host ? ownIds.filter((id) => id !== host) : ownIds;
  if (nonHost.length >= 2) return { ...(s || {}), workspaceIds: nonHost };
  return { ...(s || {}), workspaceIds: combined };
};

/* Preview thumb for the blank "Build your own" library card — a dashed frame
   with a plus, signalling "start empty". */
const PreviewBuild = () => (
  <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", background: "#fff" }}>
    <div style={{ width: "72%", height: "64%", border: "2px dashed #cdd5df", borderRadius: 12, display: "flex", alignItems: "center", justifyContent: "center", color: "#98A2B3" }}>
      <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round">
        <path d="M12 6v12M6 12h12" />
      </svg>
    </div>
  </div>
);

/*
 * ctx is provided by Homepage when rendering blocks:
 *   { kpis, isRealAdmin, heroProps }
 * Components read everything else from their own hooks, exactly as they do on
 * the static dashboard today.
 */
export const INSTANCE_SEP = "#";
/** The registry id behind a block id: "earnings#3" → "earnings". */
export const baseId = (id) =>
  (typeof id === "string" && id.includes(INSTANCE_SEP) ? id.split(INSTANCE_SEP)[0] : id);
/** True for a second-or-later copy of a component. */
export const isCopyId = (id) => typeof id === "string" && id.includes(INSTANCE_SEP);

export const WIDGETS = [
  {
    id: "hero",
    title: "Greeting",
    blurb: "Greeting, avatar and the daily quote",
    sizeHint: "Wide or full width",
    group: "Essentials",
    minW: 12,
    maxW: 24,
    minH: 15, // 15 rows = 568px — the hero card's original visible height
    maxH: 15,
    defaultW: 18,
    defaultH: 15,
    resizable: true, // width only — height is pinned by minH/maxH
    icon: IconHero,
    thumb: PreviewHero,
    renameable: false,
    render: (ctx) => <Hero {...(ctx.heroProps || {})} section="main" />,
  },
  {
    id: "mywidgets",
    title: "My Widgets",
    blurb: "Your pinned personal goals",
    sizeHint: "Narrow column",
    group: "Essentials",
    minW: 5,
    maxW: 12,
    minH: 10,
    maxH: 15,
    defaultW: 6,
    defaultH: 15,
    resizable: true,
    scroll: true,
    icon: IconWidgets,
    thumb: PreviewWidgets,
    renameable: false,
    render: (ctx) => <Hero {...(ctx.heroProps || {})} section="widgets" />,
  },
  {
    id: "kotm",
    title: "King of the Mountain",
    blurb: "You against the team, climbing for the crown",
    metricLabel: "Points",
    periods: ["Today", "Yesterday", "This Week", "Last Week", "This Month", "Last Month"],
    sizeHint: "Half or full width",
    group: "Performance",
    minW: 10,
    maxW: 24,
    minH: 11,
    maxH: 24,
    defaultW: 12,
    defaultH: 13, // 488px ≈ the mountain's natural height at half width (measured 493)
    resizable: true,
    icon: IconKotm,
    thumb: PreviewKotmPeak,
    renameable: true,
    // Two scenes, not two components. This used to be `kotm` + `kotm-photo`:
    // the same live scores drawn twice, sitting side by side in the library as
    // if they were different things to add.
    scenes: [
      { key: "photo", label: "Photo Summit", thumb: PreviewKotmPeak },
      { key: "illustrated", label: "Illustrated Peak", thumb: PreviewKotm },
    ],
    defaultScene: "photo",
    /* Scopeable like every other card: pick workspaces in the editor, and the
       climb follows the board's Viewing filter. The score is the full per-player
       average (useScopedMetrics.teamPts), which now matches the leaderboard —
       the earlier under-count that showed zero is fixed. ScopedGameboard settles
       the value before handing it over, so the climb animation plays once
       instead of restarting on every data tick. */
    scope: true,
    render: (ctx, s) => {
      const eff = withInheritedWorkspaces(s, ctx.inheritWorkspaceIds, ctx.workspaceId);
      return (
        <ScopedGameboard
          settings={eff}
          ownWorkspaceId={ctx.workspaceId || null}
          editing={baseId(ctx.editingId) === "kotm"}
          scene={s?.scene === "photo" ? "photo" : undefined}
          title={s?.title || undefined}
          boardWs={ctx.boardWs || null}
          boardWindow={ctx.boardWindow}
          boardCustom={ctx.boardCustom}
          inheritWorkspaceIds={ctx.inheritWorkspaceIds || []}
        />
      );
    },
  },
  {
    id: "yeti",
    title: "Team Performance vs. The Yeti",
    blurb: "Your metrics against the Yeti’s pace",
    metricLabel: "3 live metrics",
    periods: ["Today", "Yesterday", "This Week", "Last Week", "This Month", "Last Month"],
    sizeHint: "Flexible width",
    group: "Performance",
    minW: 8,
    maxW: 24,
    minH: 10,
    maxH: 24,
    defaultW: 12,
    defaultH: 13, // 488px — matches KOTM so the default row closes flush
    resizable: true,
    icon: IconYeti,
    thumb: PreviewYeti,
    renameable: true,
    // Only the Live metrics show on the dashboard (max 5 — same set the
    // Workspace metrics page marks with the LIVE badge).
    scope: true,
    render: (ctx, s) => (
      <ScopedCard
        settings={withInheritedWorkspaces(s, ctx.inheritWorkspaceIds, ctx.workspaceId)}
        ownWorkspaceId={ctx.workspaceId || null}
        editing={baseId(ctx.editingId) === "yeti"}
        boardWs={ctx.boardWs || null}
        boardWindow={ctx.boardWindow}
        boardCustom={ctx.boardCustom}
        inheritWorkspaceIds={ctx.inheritWorkspaceIds || []}
        title={s?.title || "Team Performance vs. The Yeti"}
      >
        {(d) => (
          <TeamYetiCard
            kpis={d.kpis}
            people={d.people}
            yetiStats={d.yeti}
            periodIdx={ctx.periodIdx}
            title={s?.title || undefined}
          />
        )}
      </ScopedCard>
    ),
    },
  {
    id: "leaderboard",
    title: "Leaderboard",
    blurb: "Every player, ranked by points",
    metricLabel: "Points",
    periods: ["Today", "Yesterday", "This Week", "Last Week", "This Month", "Last Month"],
    sizeHint: "Wide",
    group: "People",
    minW: 12,
    maxW: 24,
    minH: 10,
    maxH: 40,
    defaultW: 17, // leaves the podium enough room to breathe
    defaultH: 13, // 488px — hugs header + five rows + pager (measured 470)
    resizable: true,
    scroll: true, // the cell scrolls; the card header is sticky so only rows move
    icon: IconLeaderboard,
    thumb: PreviewLeaderboard,
    renameable: true,
    /* ONE component, two modes — the same choice wherever it is placed.
       "One workspace" is the plain board this app has always had; "Across
       workspaces" is the metric table. Only the mode in its editor decides,
       never which dashboard the card happens to sit on. */
    render: (ctx, s) => {
      /* Auto-combine: on a cross-workspace board whose HOST workspace tracks no
         live metrics of its own (e.g. Head of Sales), a plain single-workspace
         leaderboard would read that empty workspace and show nothing. Fall the
         card back to the board's selected workspaces (honouring the navbar
         Viewing pick) so it shows the combined roster — exactly what an admin
         who built a Head-of-Sales board out of other workspaces expects. */
      const combined = Array.isArray(ctx.inheritWorkspaceIds) ? ctx.inheritWorkspaceIds : [];
      const autoAcross = !s?.across && combined.length >= 2;
      /* Explicit Across (or Head-of-Sales auto-across) keeps the metric TABLE —
         that mode needs columns per shared KPI. Custom / quarter / YTD on a
         one-workspace board must KEEP the classic ProgressBar list (tabs,
         bars, Yeti) and only swap the DATA source inside LiveLeaderboard.
         Never replace that list UI with LeaderboardTable for a date filter. */
      if (s?.across || autoAcross) {
        let effIds;
        if (s?.across) effIds = s?.workspaceIds || [];
        else if (ctx.boardWs && ctx.boardWs !== "all") effIds = [ctx.boardWs];
        else effIds = combined;
        const eff = s?.across
          ? (s || {})
          : { ...(s || {}), across: true, workspaceIds: effIds };
        return (
          <LeaderboardTable
            settings={eff}
            periodIdx={ctx.periodIdx}
            boardWindow={ctx.boardWindow || null}
            boardCustom={ctx.boardCustom || null}
            title={s?.title || undefined}
            meUserId={ctx.viewingOwnerId}
          />
        );
      }
      return (
        <LeaderBoard
          isRealAdmin={ctx.isRealAdmin}
          section="list"
          periodIdx={ctx.periodIdx}
          title={s?.title || undefined}
          dashboard
          boardWindow={ctx.boardWindow || null}
          boardCustom={ctx.boardCustom || null}
          workspaceId={ctx.workspaceId || null}
        />
      );
    },
    /* Everything the cross-workspace mode needs. Offered by the editor only
       when Across is chosen, so a one-workspace board stays plain. */
    board: {
      workspaces: true,
      setup: "leaderboard",
      parts: [
        { key: "rank", label: "Rank number", group: "Columns", note: "1, 2, 3 down the left." },
        { key: "move", label: "Movement", group: "Columns", note: "Up or down against last period." },
        { key: "avatar", label: "Avatar", group: "Columns", note: "Photo or initials beside the name." },
        { key: "team", label: "Team", group: "Columns", note: "Which workspace each player is on. Needs two or more workspaces.", needs: "multi" },
        { key: "metrics", label: "Metrics", group: "Columns", note: "metrics" },
        { key: "yeti", label: "vs Yeti", group: "Columns", note: "Whether the player is beating the Yeti (or still chasing)." },
        { key: "pills", label: "Workspace filter", group: "On the card", note: "Buttons to narrow the board to one team. Needs two or more workspaces.", needs: "multi" },
        { key: "lead", label: "Highlight first place", group: "On the card", note: "Tinted top row." },
        { key: "medals", label: "Medals for the top three", group: "On the card", note: "Gold, silver, bronze instead of plain numbers." },
        { key: "pin", label: "Pin my row", group: "On the card", note: "If you fall below the cut, your row sticks to the bottom." },
      ],
    },
  },
  {
    id: "podium",
    title: "Podium",
    blurb: "The top three on their pedestals",
    metricLabel: "Points",
    periods: ["Today", "Yesterday", "This Week", "Last Week", "This Month", "Last Month"],
    sizeHint: "Narrow or wider",
    group: "People",
    minW: 5,
    maxW: 24,
    minH: 10,
    maxH: 24,
    defaultW: 7, // wide enough for three pedestals with full names
    defaultH: 13, // matches the leaderboard so the default row closes flush
    resizable: true,
    scroll: true,
    icon: IconPodium,
    thumb: PreviewPodium,
    // The podium block deliberately renders WITHOUT a title (see LeaderBoard.jsx
    // — "Standalone podium block … No title"), so there is nothing to rename.
    renameable: false,
    render: (ctx, s) => {
      /* Pedestals rank the same clipped window as the list. Across / multi-ws /
         long windows use ScopedPodium; single-ws week/month/etc go through
         LeaderBoard → LiveLeaderboard (useLeaderboardData) so pts can't drift. */
      const combined = Array.isArray(ctx.inheritWorkspaceIds) ? ctx.inheritWorkspaceIds : [];
      const autoAcross = !s?.across && combined.length >= 2;
      const needsScoped = ["custom", "quarter", "ytd", "year"].includes(ctx.boardWindow)
        || s?.across
        || autoAcross;
      if (needsScoped) {
        let eff;
        if (s?.across) eff = s || {};
        else if (autoAcross) {
          const ids = (ctx.boardWs && ctx.boardWs !== "all")
            ? [ctx.boardWs]
            : combined;
          eff = { ...(s || {}), across: true, workspaceIds: ids };
        } else {
          eff = {
            ...(s || {}),
            workspaceIds: ctx.workspaceId ? [ctx.workspaceId] : (s?.workspaceIds || []),
          };
        }
        return (
          <ScopedPodium
            settings={eff}
            workspaceId={ctx.workspaceId || null}
            boardWs={ctx.boardWs || null}
            boardWindow={ctx.boardWindow || null}
            boardCustom={ctx.boardCustom || null}
            inheritWorkspaceIds={ctx.inheritWorkspaceIds || []}
            periodIdx={ctx.periodIdx}
          />
        );
      }
      return (
        <LeaderBoard
          isRealAdmin={ctx.isRealAdmin}
          section="podium"
          periodIdx={ctx.periodIdx}
          dashboard
          boardWindow={ctx.boardWindow || null}
          boardCustom={ctx.boardCustom || null}
          workspaceId={ctx.workspaceId || null}
        />
      );
    },
  },
  {
    id: "earnings",
    money: "earnings", // gets the Setup tab: commission + bonus rules
    title: "Anticipated earnings",
    blurb: "Projected commission and bonuses for the period",
    metricLabel: "Estimate Won Value",
    periods: ["Today", "Yesterday", "This Week", "Last Week", "This Month", "Last Month"],
    sizeHint: "Half width",
    group: "Sales",
    minW: 6,
    maxW: 24,
    // Floor raised 12 -> 14 so the breakdown fits ~5 rules without scrolling —
    // an admin can compose more than the 3 the old floor assumed. conformLayout
    // snaps already-placed cards up to this, so existing boards grow to fit.
    // Past 5 rules the breakdown scrolls (see AnticipatedEarnings) rather than clip.
    minH: 10,
    maxH: 20,
    defaultW: 12,
    defaultH: 14,
    resizable: true,
    // Height follows the content: switch off the three chips or compose fewer
    // rules and the card closes up instead of leaving dead space above the
    // total. See the hug measurement in DashboardCanvas.
    hug: true,
    icon: IconEarnings,
    thumb: PreviewEarnings,
    renameable: true,
    // Optional parts an admin can switch off per board (the field card is slim,
    // In-House keeps all). The editor's Appearance tab renders a toggle each;
    // parts with `rename` also get a text input to relabel the chip on the card
    // (blank = the default shown as the input's placeholder).
    parts: [
      { key: "milestone", label: "Next milestone chip", editable: "Next milestone" },
      { key: "best", label: "Personal best chip", editable: "Personal best" },
      { key: "top", label: "Team top chip", editable: "Team top" },
      { key: "ytd", label: "Year to date row", editable: "YTD total earned" },
      { key: "trend", label: "Trend arrow" },
    ],
    // Two approved looks for the same card: which half wears the tint. Same
    // layout, same numbers — a scene, not a different component.
    scenes: [
      { key: "classic", label: "Stat chips", thumb: PreviewEarnings },
      { key: "ledger", label: "Money panel", thumb: PreviewEarningsLedger },
      { key: "rows", label: "Rows panel", thumb: PreviewEarningsRows },
    ],
    defaultScene: "classic",
    render: (ctx, s) => (
      <ScopedBonusCard
        settings={s || {}}
        workspaceId={ctx.workspaceId || null}
        fallback={ctx.bonus || null}
        globalRange={ctx.globalRange || null}
        boardWindow={ctx.boardWindow || null}
        boardCustom={ctx.boardCustom || null}
        boardWs={ctx.boardWs || null}
        boardWorkspaces={ctx.inheritWorkspaceIds || []}
      >
        {({ data, rollupOnly, window: cardWin, custom: cardCustom }) => (
          <AnticipatedEarnings
            rollupOnly={rollupOnly}
            /* The team average rides in as the FIRST rep, so an admin opens on
               "the average of the entire team" and can still pick any individual
               from the card's own picker. Players are pinned to themselves. */
            data={data}
            primary={ctx.primary}
            title={s?.title || undefined}
            hide={s?.hide}
            labels={s?.labels}
            scene={s?.scene || "classic"}
            period={cardWin || ctx.boardWindow || null}
            customRange={cardCustom || ctx.boardCustom || null}
            periodIdx={ctx.periodIdx}
            /* Default: combine multi-ws breakdown into one line. Opt into
               separate per-rule lines via Appearance / Setup. */
            breakdownSplit={!!s?.breakdownSplit}
            breakdownTitle={typeof s?.breakdownTitle === "string" ? s.breakdownTitle : ""}
            combinedPay={s?.combinedPay || null}
            bonusPrograms={s?.bonusPrograms || null}
          />
        )}
      </ScopedBonusCard>
    ),
  },
  {
    id: "milestones",
    money: "ladder", // gets the Setup tab: the bonus ladder
    title: "Milestone bonuses",
    blurb: "How close each bonus milestone is",
    metricLabel: "Estimate Won Value",
    periods: ["Today", "Yesterday", "This Week", "Last Week", "This Month", "Last Month"],
    sizeHint: "Flexible width",
    group: "Sales",
    minW: 8,
    maxW: 24,
    minH: 14, // LOCKED to the sales-row height (= Anticipated earnings) — conformLayout
    // snaps already-placed cards up to this on load, so the row always closes flush.
    // Raised 12 -> 14 in step with the earnings card so the two stay the same height.
    maxH: 24,
    defaultW: 12,
    defaultH: 14,
    resizable: true,
    icon: IconMilestones,
    thumb: PreviewMilestones,
    renameable: true,
    // The key stays neutral ("steps") so the saved data does not churn if the
    // stepped visual is ever reworked again.
    scenes: [
      { key: "steps", label: "The Tide", thumb: PreviewMilestones },
      { key: "track", label: "The Track", thumb: PreviewMilestones2 },
    ],
    defaultScene: "steps",
    render: (ctx, s) => (
      <ScopedBonusCard
        settings={s || {}}
        workspaceId={ctx.workspaceId || null}
        fallback={ctx.bonus || null}
        globalRange={ctx.globalRange || null}
        boardWindow={ctx.boardWindow || null}
        boardCustom={ctx.boardCustom || null}
        boardWs={ctx.boardWs || null}
        boardWorkspaces={ctx.inheritWorkspaceIds || []}
      >
        {({ data, rollupOnly, window: cardWin, custom: cardCustom }) => (
          <MilestoneBonuses
            rollupOnly={rollupOnly}
            data={data}
            primary={ctx.primary}
            variant={s?.scene === "track" ? "track" : "climb"}
            title={s?.title || undefined}
            period={cardWin || ctx.boardWindow || null}
            customRange={cardCustom || ctx.boardCustom || null}
          />
        )}
      </ScopedBonusCard>
    ),
  },
  {
    id: "annualcomp",
    annualSetup: true,
    title: "Projected annual compensation",
    blurb: "Year-1 total: base + prior quarters + current quarter on pace + growth kicker",
    sizeHint: "Half or full width",
    group: "Sales",
    minW: 8,
    maxW: 24,
    /* Tall enough for banner + line items + Year-1 total on the Analytics grid
       (rowHeight 40). Home hug also uses this as the floor. */
    minH: 16,
    maxH: 28,
    defaultW: 12,
    defaultH: 18,
    resizable: true,
    hug: true,
    icon: IconEarnings,
    thumb: PreviewEarnings,
    renameable: true,
    /* No scope:true — same as earnings/milestones. ScopeWindow lives in the
       editor Basics tab only. Flagging scope would also mount MetricPlan
       (Closed Rate / AVG Ticket), which this card never uses. */
    render: (ctx, s) => (
      <ScopedBonusCard
        /* No forced `window: "ytd"` — the board's navbar filter owns the window
           now (resolveCardWindow), and hard-pinning here meant this card was
           the only one on the board that never moved with it. */
        settings={{ ...(s || {}), avgMode: s?.avgMode || "total" }}
        workspaceId={ctx.workspaceId || null}
        fallback={ctx.bonus || null}
        globalRange={ctx.globalRange || null}
        boardWindow={ctx.boardWindow || null}
        boardCustom={ctx.boardCustom || null}
        boardWs={ctx.boardWs || null}
        boardWorkspaces={ctx.inheritWorkspaceIds || []}
      >
        {({ data, workspaceIds, window: cardWin, custom: cardCustom }) => (
          <ProjectedAnnualComp
            data={data}
            primary={ctx.primary}
            title={s?.title || undefined}
            settings={s || {}}
            workspaceIds={workspaceIds || []}
            period={cardWin || ctx.boardWindow || null}
            customRange={cardCustom || ctx.boardCustom || null}
          />
        )}
      </ScopedBonusCard>
    ),
  },
  {
    id: "beatyeti",
    title: "Beat the Yeti",
    blurb: "Every metric against the pace you need to beat",
    periods: ["Today", "Yesterday", "This Week", "Last Week", "This Month", "Last Month"],
    sizeHint: "Wide or full width",
    group: "Sales",
    minW: 8,
    maxW: 24,
    minH: 9,
    maxH: 24,
    defaultW: 12,
    defaultH: 10,
    resizable: true,
    scroll: true,
    icon: IconBeatYeti,
    thumb: PreviewBeatYeti,
    renameable: true,
    scenes: [
      { key: "mountains", label: "Mountains", thumb: PreviewBeatYeti },
      { key: "gates", label: "Progress bars", thumb: PreviewBeatYetiGates },
    ],
    defaultScene: "mountains",
    /* Scope · Window · Show — which workspaces, over which dates, averaged or
       totalled. The shared block (ScopeWindow.jsx), so every component that
       grows this asks the same questions the same way. */
    scope: true,
    render: (ctx, s) => (
      <ScopedBeatYeti
        settings={withInheritedWorkspaces(s, ctx.inheritWorkspaceIds, ctx.workspaceId)}
        title={s?.title || undefined}
        variant={s?.scene === "gates" ? "gates" : "mountains"}
        ownWorkspaceId={ctx.workspaceId || null}
        editing={baseId(ctx.editingId) === "beatyeti"}
        boardWs={ctx.boardWs || null}
        boardWindow={ctx.boardWindow}
        boardCustom={ctx.boardCustom}
        inheritWorkspaceIds={ctx.inheritWorkspaceIds || []}
      />
    ),
  },
  {
    id: "labor",
    title: "Payroll vs Revenue",
    blurb: "What each department costs against the share of revenue it’s allowed",
    sizeHint: "Wide or full width",
    group: "Sales",
    minW: 10,
    maxW: 24,
    minH: 9,
    maxH: 30,
    /* Nine columns need the room — narrower still works (the department column
       sticks while the rest scrolls), but this is the width they all fit in. */
    defaultW: 16,
    defaultH: 11,
    resizable: true,
    scroll: true,
    icon: IconLabor,
    thumb: PreviewLabor,
    renameable: true,
    /* Departments, their pay rates and their goals are picked per ROW in Setup,
       so this card never takes the editor's single-workspace dropdown. */
    laborSetup: true,
    /* The board's own date filter when it sets one, otherwise the period the
       page is on — the card must never sit on "this month" while the board
       around it reads the quarter. */
    render: (ctx, s, id) => (
      <LaborMetrics
        settings={s || {}}
        title={s?.title || undefined}
        boardWindow={ctx.boardWindow || ctx.periodName || null}
        boardCustom={ctx.boardCustom || null}
        blockId={id}
        saveSettings={ctx.saveWidgetSettings}
      />
    ),
    parts: [
      { key: "summary", label: "Summary strip", group: "On the card", note: "Total revenue, total cost and the payroll total above the rows." },
      { key: "rail", label: "Progress rails", group: "On the card", note: "The bar under each performance figure, with a notch at the goal." },
      { key: "totals", label: "Payroll total row", group: "On the card", note: "Every department added up. Needs two or more." },
      { key: "hours", label: "Hours", group: "Columns" },
      { key: "pay", label: "Pay / hour", group: "Columns", note: "The average of each employee's own rate — expand a department to set them." },
      { key: "cost", label: "Total cost", group: "Columns" },
      { key: "delta", label: "Over / Under", group: "Columns", note: "How many points off its goal the department is." },
      { key: "deltaAmt", label: "Over / Under $", group: "Columns", note: "The same gap in dollars." },
      { key: "goal", label: "Goal", group: "Columns" },
      { key: "goalAmt", label: "Goal $", group: "Columns", note: "What that share of revenue comes to." },
    ],
  },
  {
    id: "goalProgress",
    title: "Goal Progress",
    blurb: "Pick a metric, set a goal, watch the trend line close the gap",
    sizeHint: "Half or full width",
    group: "Performance",
    minW: 10,
    maxW: 24,
    minH: 11,
    maxH: 20,
    defaultW: 14,
    defaultH: 13,
    resizable: true,
    icon: IconGoal,
    thumb: PreviewGoalProgress,
    renameable: true,
    /* Single-metric picker + a numeric goal, gated in ComponentEditor's Basics
       tab the same way isLabor/isMoney gate their own extra fields. */
    goalCard: true,
    scope: true,
    /* Appearance's generic "What to show" toggles (ComponentEditor already
       renders a switch per entry here for any def.parts — see the isBoard
       block it was built for) — decorative pieces only; Set/Edit Goal and
       the metric itself are never hideable from here. */
    parts: [
      { key: "period", label: "Period label under the number", group: "On the card" },
      { key: "pct", label: "Percent / \"Goal reached\" chip", group: "On the card" },
      { key: "goalline", label: "Goal line on the chart", group: "Chart" },
      { key: "pace", label: "Caption under the chart", group: "Chart", note: "The goal per working day and whether today is keeping up, plus anything earned on a day the chart isn't drawing." },
      { key: "tooltip", label: "Hover tooltip on the chart", group: "Chart" },
    ],
    render: (ctx, s, id) => (
      <GoalProgress
        settings={s || {}}
        title={s?.title || undefined}
        boardWindow={ctx.boardWindow || ctx.periodName || null}
        boardCustom={ctx.boardCustom || null}
        blockId={id}
        saveSettings={ctx.saveWidgetSettings}
        workspaceId={ctx.workspaceId || null}
      />
    ),
  },
  {
    id: "profit",
    title: "Revenue, Expenses & Labor",
    blurb: "The three lines over time, and what's left after them",
    sizeHint: "Wide or full width",
    group: "Sales",
    minW: 10,
    maxW: 24,
    minH: 9,
    maxH: 26,
    /* Four tiles above a plot: narrower than this and the tiles wrap to two
       rows, which works but leaves the chart the loser. */
    defaultW: 16,
    defaultH: 11,
    resizable: true,
    icon: IconProfit,
    thumb: PreviewProfit,
    renameable: true,
    /* Revenue, labor, expenses and every goal are picked in this card's own
       Setup, so it takes none of the editor's single-workspace dropdown. */
    profitSetup: true,
    /* The board's own date filter when it sets one, otherwise the period the
       page is on — the card must never sit on "this month" while the board
       around it reads the quarter. `boardWidgets` is what lets Labor be read
       from a Payroll vs Revenue card sitting beside it. */
    render: (ctx, s) => (
      <ProfitTrend
        settings={s || {}}
        title={s?.title || undefined}
        boardWindow={ctx.boardWindow || ctx.periodName || null}
        boardCustom={ctx.boardCustom || null}
        boardWidgets={ctx.boardWidgets || []}
      />
    ),
    parts: [
      { key: "tiles", label: "Summary tiles", group: "On the card", note: "The four figures above the chart." },
      { key: "revenue", label: "Revenue", group: "Series", editable: "Revenue", note: "Its tile. Whether it's plotted is under Setup." },
      { key: "net", label: "Net profit", group: "Series", editable: "Net profit" },
      { key: "expenses", label: "Expenses", group: "Series", editable: "Expenses", note: "Non-payroll costs only — payroll ones count toward Labor." },
      { key: "labor", label: "Labor", group: "Series", editable: "Labor" },
      { key: "legend", label: "Legend", group: "Chart" },
      { key: "grid", label: "Grid lines", group: "Chart" },
      { key: "dots", label: "Point markers", group: "Chart" },
      { key: "goalLines", label: "Goal lines", group: "Chart", note: "The dashed limit drawn across the plot." },
      { key: "loss", label: "Loss shading", group: "Chart", note: "Shades the gap between Revenue and what the day cost, on the days revenue came in under it." },
      { key: "pace", label: "Pace line", group: "Chart", note: "Revenue goal ÷ the period's working days — what a day has to bring. Needs a Revenue goal in dollars, under Setup." },
      { key: "depts", label: "Departments on hover", group: "Chart", note: "Each department's share of revenue at the point under the pointer." },
    ],
  },
  {
    id: "netbonus",
    title: "Net Profit Bonus",
    blurb: "Reach a net profit, earn a bonus; reach a higher one, earn more",
    sizeHint: "Half or full width",
    group: "Sales",
    minW: 8,
    maxW: 24,
    /* Head, two figures, the ladder and the next-step panel come to about 410px
       (440 with "Both"): the smallest row count that holds it. Shorter still
       scrolls the body instead of clipping. */
    minH: 12,
    maxH: 26,
    defaultW: 12,
    defaultH: 13,
    resizable: true,
    scroll: true,
    icon: IconNetBonus,
    thumb: PreviewNetBonus,
    renameable: true,
    /* The card reads its Net profit from a Revenue, Expenses & Labor card on the
       same board, so it needs the board's other cards (`boardWidgets`) and takes
       none of the editor's single-workspace dropdown. Analytics dashboards only:
       a Home board has no place to keep its settings (settingsFor below names no
       netBonus key), so the Home library leaves it out. */
    netBonusSetup: true,
    analyticsOnly: true,
    render: (ctx, s) => (
      <NetProfitBonus
        settings={s || {}}
        title={s?.title || undefined}
        boardWindow={ctx.boardWindow || ctx.periodName || null}
        boardCustom={ctx.boardCustom || null}
        boardWidgets={ctx.boardWidgets || []}
        primary={ctx.primary || undefined}
      />
    ),
    parts: [
      { key: "steps", label: "Step boxes", group: "On the card", note: "Each line and what it pays, under the bar." },
      { key: "next", label: "What unlocks the next step", group: "On the card", note: "The panel showing how far each line still has to go." },
    ],
  },
  {
    id: "deptmetrics",
    title: "All Department Metrics",
    blurb: "Every team's own metrics, green or red on beating the Yeti",
    sizeHint: "Wide or full width",
    group: "Sales",
    minW: 10,
    maxW: 24,
    minH: 8,
    maxH: 30,
    /* A row of five cells needs the room. Narrower still works — the
       department name moves above its own row of cells — but this is the
       width they read side by side in. */
    defaultW: 16,
    defaultH: 12,
    resizable: true,
    scroll: true,
    icon: IconDeptMetrics,
    thumb: PreviewDeptMetrics,
    renameable: true,
    /* Departments and their metrics are picked per ROW in Setup, so this card
       never takes the editor's single-workspace dropdown. */
    deptSetup: true,
    /* The board's own date filter when it sets one, otherwise the period the
       page is on — the card must never sit on "this month" while the board
       around it reads the quarter. */
    render: (ctx, s) => (
      <DeptMetrics
        settings={s || {}}
        title={s?.title || undefined}
        boardWindow={ctx.boardWindow || ctx.periodName || null}
        boardCustom={ctx.boardCustom || null}
      />
    ),
    parts: [
      { key: "summary", label: "Headline count", group: "On the card", note: "How many metrics are beating him across every department." },
      { key: "rowStatus", label: "Per-row count", group: "On the card", note: "The “3/5 beating” beside each department's name." },
      { key: "value", label: "Figures on the tiles", group: "On the card", note: "Off leaves just the climb and its colour — readable from further away." },
      { key: "delta", label: "Gap to the Yeti", group: "On the card", note: "The ± figure under each number: how far ahead of, or behind, his pace." },
    ],
  },
  {
    // The blank "Build your own" card. It is NEVER placed on the board itself —
    // clicking it opens the create flow, which mints a real `custom-<id>` block
    // (see makeCustomId). So it stays permanently addable, never shows "Added".
    id: "custom",
    blank: true,
    title: "Create a component",
    blurb: "Pick a metric, choose how it’s drawn",
    sizeHint: "Quarter or half width",
    group: "Your own",
    icon: IconBuild,
    thumb: PreviewBuild,
    renameable: false,
  },
];

/* ── Custom "Build your own" widgets ───────────────────────────────────────
   Unlike the fixed cards above, these are per-instance: an admin can add many,
   each with its own name, metric, and graph. Their ids carry a prefix so the
   registry can recognise them and synthesise a def on the fly — the metric and
   graph live in the block's saved settings (`s`), not the def. */
export const CUSTOM_PREFIX = "custom-";
export const isCustomId = (id) => typeof id === "string" && id.startsWith(CUSTOM_PREFIX);
export const makeCustomId = () =>
  `${CUSTOM_PREFIX}${Date.now().toString(36)}${Math.floor(Math.random() * 46656).toString(36)}`;

/** A custom widget's metric rows. Accepts the new `metrics: [{metricId, graph}]`
    shape and the original single `metricId`/`graph` pair, so widgets built
    before two-metric support keep rendering. */
export const metricsOf = (s) => {
  if (s && Array.isArray(s.metrics) && s.metrics.length) {
    return s.metrics
      .filter((m) => m && m.metricId)
      .map((m) => ({ metricId: m.metricId, graph: m.graph || "number", color: m.color || null }));
  }
  if (s && s.metricId) return [{ metricId: s.metricId, graph: s.graph || "number", color: null }];
  return [];
};

/* A metric's colour, picked in Visualisation beside its graph. One swatch drives
   the target bar, the sparkline and the ring together — a metric is one visual
   decision, not three. Kept to a fixed set so a board cannot end up with
   forty hand-typed hexes nobody can keep straight. */
export const METRIC_COLORS = ["#1B598E", "#EC2786", "#0E9384", "#F5B301", "#7A5AF8", "#12A150", "#B54708"];

/* Colour sentinels + resolver live in a leaf module (helpers/metricColor) so the
   renderers can import them without depending on this registry — importing back
   into widgetRegistry formed a cycle that failed the CI build. Re-exported here
   so existing `from "./widgetRegistry"` imports keep working. */
export {
  NO_COLOR,
  NEUTRAL_METRIC_COLOR,
  COMPANY_COLOR,
  GOAL_COLOR,
  GOAL_MET_COLOR,
  GOAL_MISS_COLOR,
  resolveMetricColor,
} from "../../../helpers/metricColor";

/* What can sit under the numbers. Both read data that is already deployed:
   the ranking from the leaderboard's own source, the activity list from
   /ekpi/users/:companyId?kpiId= which Scorekeeper already uses.

   One at a time, never both — two blocks push the card past the height of
   whatever sits beside it in the row, and then it dictates the whole row. */
export const SHOW_OPTIONS = [
  { key: "", title: "Nothing", blurb: "Just the numbers. The card stays short." },
  { key: "ranking", title: "Team ranking", blurb: "Where everyone stands on this metric. Opens on the player's own row." },
  { key: "activity", title: "Latest activity", blurb: "The jobs or sales that made the number." },
];

/* When the card is scored on more than one metric, the block underneath has to
   be told which one it is about. Left to itself the code takes the first row,
   which is a silent decision nobody reading the card could account for. */
export const showOnOf = (s) => {
  const rows = metricsOf(s);
  if (!rows.length) return null;
  const picked = s && s.showOn;
  return rows.some((r) => r.metricId === picked) ? picked : rows[0].metricId;
};

/* ── How wide a custom component should be ─────────────────────────────────
   Width was fixed at a quarter no matter what went inside, so one number sat in
   a card with room for four, and two metrics with graphs got squeezed into the
   same quarter. The content decides instead: a single figure is a quarter, and
   anything richer than that — a second metric, or the bonus box — earns a half.

   Three widths exist and no others (WIDTH_STEPS). Someone arranging a board
   should be choosing between a quarter and a half, not dragging out a 7-column
   card and then measuring the gap next to it. */
export const WIDTH_STEPS = [6, 12, 24]; // quarter · half · full, on the 24-col grid
export const customWidthFor = (s) => {
  const rows = metricsOf(s);
  const rich = rows.length > 1 || !!(s && s.bonus) || !!(s && s.payoutCurve && s.payoutCurve.on);
  return rich ? 12 : 6;
};
/** Nearest legal width — what a resize drag lands on. */
export const snapWidth = (w) =>
  WIDTH_STEPS.reduce((best, step) => (Math.abs(step - w) < Math.abs(best - w) ? step : best), WIDTH_STEPS[0]);

const customDef = (id) => ({
  id,
  custom: true,
  title: "New component",
  blurb: "A metric you chose yourself",
  group: "Your own",
  minW: 6, // a quarter is the floor — narrower and the number itself wraps
  maxW: 24,
  minH: 5,
  maxH: 20,
  defaultW: 6, // a quarter; customWidthFor() promotes it to a half when earned
  defaultH: 10,
  widthSteps: WIDTH_STEPS, // resizing snaps to these, so no odd in-between widths
  resizable: true,
  hug: true, // one metric, two, plus a bonus — the card is only as tall as what's in it

  renameable: true,
  /* ScopedCustomWidget wraps CustomWidget: plain when unscoped (rep's own value,
     admin's board-workspace average — unchanged), but a team average across the
     picked workspaces once scoped. The picker for ADMINS only (`who`), gated on
     the viewer's role, and off entirely when scoped so a team view can't swap to
     one person. `period` unset = follow the dashboard filter. */
  render: (ctx, s) => (
    <ScopedCustomWidget
      ctx={ctx}
      s={s || {}}
      widget={{
        seriesByDay: ctx.seriesByDay,
        metrics: metricsOf(s),
        bonus: s?.bonus || null,
        payoutCurve: s?.payoutCurve || null,
        pieceLayout: s?.pieceLayout || null,
        look: s?.look || null,
        title: s?.title || undefined,
        primary: ctx.primary,
        show: s?.show || "",
        showOn: showOnOf(s),
        period: s?.period || ctx.periodName || "month",
        ownerId: ctx.viewingOwnerId || null,
        loading: !!ctx.kpisLoading,
        who: s?.who,
        isRealAdmin: ctx.isRealAdmin,
      }}
    />
  ),
});

/* ── layouts a custom widget can wear ──────────────────────────────────────
   These are GENERIC: each one adapts to however many metrics were added and
   whether a bonus exists. `need` pins a layout to an exact metric count (0 =
   any); `bonus: true` means it only makes sense once a bonus is set. */
/** How many metrics one custom component can hold. The card tiles, so the
    ceiling is about legibility — six still fits a half-card grid without
    collapsing the number + graph. */
export const MAX_METRICS = 6;

export const LAYOUTS = [
  { key: "tiles", label: "Tiles + payoff", blurb: "Metrics side by side, earnings below.", min: 2, bonus: true },
  { key: "meter", label: "Earnings meter", blurb: "The money leads, a bar tracks pace.", min: 0, bonus: true },
  { key: "focus", label: "Focus", blurb: "One number owns the card.", min: 0, bonus: false },
  { key: "duo", label: "Grid", blurb: "Every metric, nothing else.", min: 2, bonus: false },
];

/** Which layouts fit what the admin has actually built. First is recommended. */
export const layoutsFor = (rows = [], bonus = null) => {
  const n = rows.length;
  if (!n) return [];
  const rule = sanitizeBonus(bonus);
  const out = LAYOUTS.filter((L) => {
    if (L.bonus && !rule) return false;
    /* The meter leads with a commission's running total; a step bonus has a
       ladder, not a total to fill a bar with, and draws under the numbers. */
    if (L.key === "meter" && isStepBonus(rule)) return false;
    if (L.min && n < L.min) return false;
    return true;
  });
  return out.length ? out : [LAYOUTS.find((L) => L.key === "focus")];
};

/** Smart-default graph for a metric, by its kind: % → ring, $ with a goal →
    target bar, everything else → the plain number. */
export const defaultGraphFor = (metric) => {
  if (!metric) return "number";
  const kind = metric.kind === "$" || metric.kind === "%" ? metric.kind : "#";
  if (kind === "%") return "ring";
  if (kind === "$" && Number(metric.target) > 0) return "target";
  return "number";
};

/** The graph choices offered in the create/edit flow. All draw from the current
    value + its target — no fabricated history — so every option shows a real
    number. (Trend charts like line/area need the rep's daily series, which the
    dev exposes separately; they're intentionally not offered until it exists.) */
export const GRAPH_OPTIONS = [
  { value: "number", label: "Number" },
  { value: "target", label: "Target bar" },
  { value: "ring", label: "Progress ring" },
  { value: "donut", label: "Donut" },
  { value: "gauge", label: "Gauge" },
  { value: "bar", label: "Bar", series: true },
  { value: "area", label: "Area", series: true },
  { value: "line", label: "Line", series: true },
  { value: "spark", label: "Sparkline", series: true },
];
export const GRAPH_VALUES = GRAPH_OPTIONS.map((o) => o.value);

/* ── More than one of the same component ──────────────────────────────────
   A board may hold two Anticipated earnings cards, three leaderboards, and so
   on. Extra copies carry an instance suffix — `earnings`, `earnings#2`,
   `earnings#3` — so every block still has a unique grid id (react-grid-layout
   keys on it, and the layout store de-duplicates on it) while resolving to the
   same definition. Settings live per block, so each copy is configured on its
   own: different workspaces, different date range.

   This mirrors what custom widgets already do with their `custom-` ids; it just
   extends the idea to the built-ins. */

/** The next free id for `base` on this layout: earnings → earnings#2 → #3 … */
export const makeInstanceId = (base, layout = []) => {
  const taken = new Set((layout || []).map((b) => b && b.i));
  if (!taken.has(base)) return base;
  for (let n = 2; n < 100; n += 1) {
    const candidate = `${base}${INSTANCE_SEP}${n}`;
    if (!taken.has(candidate)) return candidate;
  }
  return `${base}${INSTANCE_SEP}${Date.now().toString(36)}`;
};

/** How many copies of `base` are on this layout. */
export const copiesOf = (base, layout = []) =>
  (layout || []).filter((b) => b && baseId(b.i) === base).length;

export const widgetById = (id) =>
  WIDGETS.find((w) => w.id === baseId(id)) || (isCustomId(id) ? customDef(id) : null);

/** Does this component expose anything to edit beyond its position on the grid? */
export const isEditable = (id) => {
  const def = widgetById(id);
  return !!def && (def.renameable || (def.scenes || []).length > 1);
};

/** A block's saved settings, filled in with the registry's defaults. */
export const settingsFor = (block) => {
  const def = widgetById(block?.i);
  if (!def) return {};
  const s = block?.s || {};
  // Game + money components default to following the board date filter;
  // pin a window in the editor to override.
  const windowDefault = "follow";
  const WINDOW_IDS = ["follow", "today", "yesterday", "week", "lastWeek", "lastMonth", "month", "quarter", "ytd", "year", "custom"];
  return {
    title: s.title || "",
    scene: s.scene || def.defaultScene || (def.scenes?.[0]?.key ?? null),
    // "" means "follow the dashboard's period filter" — a per-card override
    // only exists once you pick one.
    period: s.period || "",
    customRange: s.customRange && typeof s.customRange === "object" ? s.customRange : null,
    // Which optional parts of the card are switched off. { partKey: true } = hidden.
    // Default {} = everything shows, so untouched cards are unaffected.
    hide: s.hide && typeof s.hide === "object" ? s.hide : {},
    // Per-board custom chip titles ({ partKey: "New name" }). Empty/absent =
    // the built-in label. Kept alongside `hide` and normalized the same way.
    labels: s.labels && typeof s.labels === "object" ? s.labels : {},
    // "Build your own" widgets: their metric rows, optional bonus, and layout.
    metrics: metricsOf(s),
    bonus: s.bonus && typeof s.bonus === "object" ? s.bonus : null,
    /* A settable payout curve (Advanced): tiers of threshold → payout, %  or $,
       driven by the component's metric. Null unless the admin turns it on. */
    payoutCurve: sanitizePayoutCurve(s.payoutCurve),
    /* How the metric and curve sit together (direction · swap · split). */
    pieceLayout: sanitizePieceLayout(s.pieceLayout),
    goalTarget: (() => {
      const g = Number(s.goalTarget);
      return Number.isFinite(g) && g > 0 ? g : null;
    })(),
    /* Technicians' Revenue per Hour's goal ($/hr), typed on the card. Null =
       keep borrowing the company's own anchor goal. */
    rphGoal: Number(s.rphGoal) > 0 && Number.isFinite(Number(s.rphGoal)) ? Number(s.rphGoal) : null,
    look: s.look || null,
    /* Goal Progress's own two line colours (Appearance) — see layoutStore's
       sanitizeSettings for the hex validation this mirrors. */
    lineColor: typeof s.lineColor === "string" ? s.lineColor : null,
    goalColor: typeof s.goalColor === "string" ? s.goalColor : null,
    chartStyle: GRAPH_VALUES.includes(s.chartStyle) ? s.chartStyle : "area",
    /* Goal Progress's trend: the running total (the original, still the
       default) or each day on its own, and whether the goal is drawn as a
       working-day pace or a flat rule at the goal itself. */
    chartView: s.chartView === "daily" ? "daily" : "cumulative",
    goalLineMode: s.goalLineMode === "flat" ? "flat" : "pace",
    /* All Department Metrics: each tile as the Yeti climb (default) or a plain
       progress bar. */
    tileStyle: s.tileStyle === "bar" ? "bar" : "climb",
    /* Which days get a column: every one of them, or only the days the company
       trades on (Mon–Fri minus company holidays). */
    chartDays: s.chartDays === "working" ? "working" : "all",
    /* Total Scheduled Revenue's look-ahead, in days; 0 is off. */
    lookAhead: normalizeLookAhead(s.lookAhead),
    /* Beat the Yeti rate: a "7/10 beating" row per tracked workspace, an overall
       bar, and a goal notch. (A card saved with the older hand-picked `yetiTeams`
       list reads as "by team" and drops that list on its next save.) */
    yetiByTeam: yetiByTeamOn(s),
    yetiBar: s.yetiBar === true,
    yetiGoal: normalizeYetiGoal(s.yetiGoal),
    /* Per-workspace goals ({ [workspaceId]: percent }); a team without one uses
       the overall goal. */
    yetiGoals: normalizeYetiGoals(s.yetiGoals),
    /* The gap between the Y-axis labels; null is Auto. */
    yStep: Number(s.yStep) > 0 && Number.isFinite(Number(s.yStep)) ? Number(s.yStep) : null,
    /* What sits under the numbers, and which metric it reads. This function
       REBUILDS settings key by key rather than spreading them, so a field that
       is not named here is gone with no error — which is exactly how `show`
       arrived at the card as "" after being set correctly in the panel. Any new
       setting has to be added in three places: here, `sanitizeSettings` in
       layoutStore, and the server's `cleanLayout`. */
    show: s.show || "",
    showOn: s.showOn || null,
    // Admins can switch which player the card is about; a player never can.
    who: s.who !== false,
    /* ── scope · window · average ─────────────────────────────────────────
       Empty list = this board's own workspace, which is what every card did
       before any of this existed, so an untouched card is unchanged. */
    workspaceIds: Array.isArray(s.workspaceIds) ? s.workspaceIds.map(String) : [],
    filterBy: s.filterBy === "player" ? "player" : "workspace",
    /* "follow" = board date filter; any other id pins the card. */
    window: WINDOW_IDS.includes(s.window) ? s.window : windowDefault,
    avgMode: s.avgMode === "total" ? "total" : "avg",
    cardPick: s.cardPick || "all",
    /* Cross-workspace leaderboard mode. Omitted here → reopen + Save Changes
       stripped `across` from the draft and the board fell back to reading the
       empty host (Head of Sales → blank roster). */
    across: s.across === true,
    /* Which metrics combine across the chosen workspaces. Same reopen/save
       wipe as `across` — without this the metric-plan editor looked empty and
       Save Changes dropped the plan back to name-match. */
    metricPlan: s.metricPlan && typeof s.metricPlan === "object" ? s.metricPlan : null,
    baseSalary: Number.isFinite(Number(s.baseSalary)) ? Number(s.baseSalary) : undefined,
    priorBonuses: Number.isFinite(Number(s.priorBonuses)) ? Number(s.priorBonuses) : undefined,
    q4Pace: Number.isFinite(Number(s.q4Pace)) ? Number(s.q4Pace) : undefined,
    kickerEarned: Number.isFinite(Number(s.kickerEarned)) ? Number(s.kickerEarned) : undefined,
    kickerUpside: Number.isFinite(Number(s.kickerUpside)) ? Number(s.kickerUpside) : undefined,
    ceiling: Number.isFinite(Number(s.ceiling)) ? Number(s.ceiling) : undefined,
    targetLow: Number.isFinite(Number(s.targetLow)) ? Number(s.targetLow) : undefined,
    targetHigh: Number.isFinite(Number(s.targetHigh)) ? Number(s.targetHigh) : undefined,
    /* goalTarget + payoutCurve are set above (shared with Build-your-own). */
    useLiveKicker: s.useLiveKicker !== false,
    useLivePrior: s.useLivePrior !== false,
    useLiveCurrent: s.useLiveCurrent !== false,
    revenueMetricId: typeof s.revenueMetricId === "string" ? s.revenueMetricId : undefined,
    revenueMetricLabel: typeof s.revenueMetricLabel === "string" ? s.revenueMetricLabel : undefined,
    revenueMetricIds: Array.isArray(s.revenueMetricIds) ? s.revenueMetricIds.map(String) : undefined,
    revenueColumnKeys: Array.isArray(s.revenueColumnKeys) ? s.revenueColumnKeys.map(String) : undefined,
    /* Payroll vs Revenue (Labor): one department per array entry. Not named
       at all until now, which silently rebuilt every Labor card as empty the
       moment it read through this function instead of a live edit draft. */
    rows: Array.isArray(s.rows) ? s.rows : undefined,
    revenue: Number.isFinite(Number(s.revenue)) ? Number(s.revenue) : undefined,
    revenueWorkspaceId: typeof s.revenueWorkspaceId === "string" ? s.revenueWorkspaceId : undefined,
    revenueKpiId: typeof s.revenueKpiId === "string" ? s.revenueKpiId : undefined,
    /* Revenue, Expenses & Labor's "Working days only" axis toggle. */
    grain: ["auto", "day", "week", "month"].includes(s.grain) ? s.grain : undefined,
    workingDaysOnly: s.workingDaysOnly === true ? true : undefined,
  };
};

/* ── Card size ─────────────────────────────────────────────────────────────
   Three presets on the 24-column grid, clamped to each component's own limits.
   Size lives on the block's w/h (the grid is the source of truth), NOT in `s`,
   so dragging the resize handle and picking a preset are the same edit. */
export const SIZE_KEYS = ["compact", "standard", "feature"];
const clampW = (def, w) => Math.min(def.maxW, Math.max(def.minW, w));
const clampH = (def, h) => Math.min(def.maxH, Math.max(def.minH, h));
export const sizeDims = (id, key) => {
  const def = widgetById(id);
  if (!def) return null;
  if (key === "compact") return { w: clampW(def, def.minW), h: clampH(def, def.minH) };
  if (key === "feature") return { w: clampW(def, GRID_COLS), h: clampH(def, def.defaultH + 4) };
  return { w: clampW(def, def.defaultW), h: clampH(def, def.defaultH) };
};
/** Which preset a placed block is nearest to (for the active state). */
export const sizeKeyOf = (block) => {
  const def = widgetById(block?.i);
  if (!def) return "standard";
  const dist = (k) => {
    const d = sizeDims(block.i, k);
    return Math.abs(block.w - d.w) + Math.abs(block.h - d.h);
  };
  return SIZE_KEYS.slice().sort((a, b) => dist(a) - dist(b))[0];
};

/** Period label → the dashboard's periodIdx (mirrors Homepage PERIOD_OPTIONS). */
export const PERIOD_TO_IDX = {
  Today: 1, today: 1,
  Yesterday: 0, yesterday: 0,
  "This Week": 2, week: 2,
  "Last Week": 4, lastWeek: 4,
  "Last Month": 5, lastMonth: 5,
  "This Month": 3, month: 3,
};

/** Mirrors the original static dashboard — what a workspace sees before anyone customizes. */
export const DEFAULT_LAYOUT = [
  { i: "hero", x: 0, y: 0, w: 18, h: 15 },
  { i: "mywidgets", x: 18, y: 0, w: 6, h: 15 },
  { i: "kotm", x: 0, y: 15, w: 12, h: 13 },
  { i: "yeti", x: 12, y: 15, w: 12, h: 13 },
  { i: "leaderboard", x: 0, y: 28, w: 17, h: 13 },
  { i: "podium", x: 17, y: 28, w: 7, h: 13 },
];

/* Grid rows that NO block crosses are dead vertical space: with
   preventCollision on and compaction off, a board dragged around in edit mode
   can end up published with screens of empty band between rows, and nothing
   ever pulls it back up. Shift everything below each empty band up by the
   band's height — blocks keep their row-mates and their column, only the gaps
   between rows close. (A block can never straddle a band: its own span covers
   it, so per-band shifting cannot create collisions.) */
const closeEmptyBands = (blocks) => {
  if (!blocks.length) return blocks;
  const spans = blocks.map((b) => [b.y, b.y + b.h]).sort((a, b) => a[0] - b[0]);
  const merged = [];
  spans.forEach(([s, e]) => {
    const last = merged[merged.length - 1];
    if (last && s <= last[1]) last[1] = Math.max(last[1], e);
    else merged.push([s, e]);
  });
  let gap = merged[0][0]; // empty space above the very first row counts too
  const shiftAt = merged.map(([s], i) => {
    if (i > 0) gap += s - merged[i - 1][1];
    return { start: s, gap };
  });
  if (!shiftAt.some((m) => m.gap > 0)) return blocks;
  return blocks.map((b) => {
    const m = shiftAt.filter((x) => x.start <= b.y).pop();
    return m && m.gap ? { ...b, y: b.y - m.gap } : b;
  });
};

/** Drop unknown ids (stale saves), clamp each block to its constraints, and
    close the empty bands between rows. */
export const conformLayout = (layout) =>
  closeEmptyBands(
    (Array.isArray(layout) ? layout : [])
      .filter((b) => widgetById(b.i))
      .map((b) => {
        const def = widgetById(b.i);
        /* For a custom component the width its content earns is a FLOOR, not a
           fixed value: you can always give a card more room, but two metrics and
           a bonus can never be crushed back into a quarter. This is also what
           repairs boards built before the rule existed — a card that was born a
           quarter and later grew a second metric widens on the next load. */
        const floor = def.custom ? Math.max(def.minW, customWidthFor(b.s)) : def.minW;
        const w = Math.min(Math.max(b.w, floor), def.maxW);
        const h = Math.min(Math.max(b.h, def.minH), def.maxH);
        return { ...b, w, h, x: Math.min(b.x, GRID_COLS - w) };
      }),
  );

